import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

// P3-3 / P3-4 — one list of shortfall reasons (fld2.SHORTFALL_REASONS) with what each raises for the PM.
export const REASONS = [
  ["missing", "Missing", "Re-order?"], ["not_on_hand", "Not on hand", "Re-order?"], ["not_ordered", "Not ordered", "Re-order?"],
  ["backordered", "Backordered", "Expedite the PO?"], ["delivered_earlier", "Delivered earlier", "Link the earlier ticket"],
  ["damaged", "Damaged", "Write off + re-order?"], ["substituted", "Substituted", "Substitute review"], ["other", "Other", "Note to the PM"],
];
export const reasonLabel = (v) => REASONS.find((r) => r[0] === v)?.[1] || v || "—";

/** Dark-safe reason picker; opens the moment the line is marked Partial (no waiting for a typed qty). */
export function ShortfallReasonSelect({ value, onChange, disabled, testId, className = "h-7 text-[11px]" }) {
  return (
    <Select value={value || ""} onValueChange={(v) => onChange(v === "__none" ? "" : v)} disabled={disabled}>
      <SelectTrigger className={`${className} w-full min-w-[120px] bg-card`} data-testid={testId} data-value={value || ""}>
        <SelectValue placeholder="Reason…">{value ? reasonLabel(value) : "Reason…"}</SelectValue>
      </SelectTrigger>
      <SelectContent data-testid={`${testId}-list`}>
        <SelectItem value="__none">—</SelectItem>
        {REASONS.map(([v, l, raises]) => (
          <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>
            <span>{l}</span><span className="ml-2 text-[10px] text-muted-foreground">{raises}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Every bin this product sits in, with how many are there. */
export function BinList({ bins = [], testId, className = "" }) {
  if (!bins.length) return null;
  return (
    <div className={`font-mono leading-tight text-[10px] text-slate-600 dark:text-slate-300 ${className}`} data-testid={testId}
         title="Bins recorded when this was put away">
      {bins.map((b) => (
        <span key={b.bin} className="mr-1.5 inline-block whitespace-nowrap">{b.bin}<span className="text-muted-foreground"> ×{b.qty % 1 ? b.qty.toFixed(1) : b.qty}</span></span>
      ))}
    </div>
  );
}

const OUTCOME = {
  reorder: [["reorder", "Re-order"], ["no_reorder", "No re-order"]],
  expedite: [["reorder", "Expedite — re-order"], ["noted", "Noted — leave as is"]],
  link_delivery: [["linked", "Confirmed — delivered earlier"], ["reorder", "Not delivered — re-order"]],
  writeoff_reorder: [["writeoff", "Write off + re-order"], ["noted", "Noted — write off only"]],
  substitute_review: [["accepted", "Accepted — substitute OK"], ["reorder", "Re-order the right one"]],
  note: [["noted", "Noted"]],
};

/** The open action item behind a short line — the PM resolves it here or from the bell. */
export function ActionPill({ action, onChanged, testId }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  if (!action) return null;
  const done = action.status !== "open";
  const resolve = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/warehouse/actions/${action.id}/resolve`, { outcome, note: "" });
      toast.success(`${action.title} — ${OUTCOME[action.kind]?.find((o) => o[0] === outcome)?.[1] || outcome}`);
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resolve the action"); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} data-status={action.status}
                className={`inline-flex h-5 items-center gap-1 rounded-full border px-1.5 text-[10px] font-semibold whitespace-nowrap ${
                  done ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                       : "border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-500 dark:bg-amber-900/40 dark:text-amber-200"}`}>
          <ClipboardList className="h-3 w-3" /> {(action.product || "").split(" ")[0]} · {done ? "Resolved" : action.title}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} className="w-80 max-w-[calc(100vw-24px)] p-3 text-xs" data-testid={`${testId}-pop`}>
        <div className="font-semibold text-foreground">{action.title}</div>
        <div className="mt-0.5 text-muted-foreground">
          {action.product} · short {action.qty_short} {action.uom} · {reasonLabel(action.reason)} · due {action.due}
        </div>
        {action.linked_ticket && <div className="mt-1">Earlier ticket: <b>{action.linked_ticket.ticket_number}</b></div>}
        {action.substitute && <div className="mt-1">Substitute: <b>{action.substitute}</b></div>}
        {action.note && <div className="mt-1 whitespace-pre-wrap">{action.note}</div>}
        <div className="mt-1 text-[10px] text-muted-foreground">To {action.assigned_to?.join(", ") || "nobody yet"} · raised by {action.raised_by}</div>
        {done ? (
          <div className="mt-2 text-emerald-700 dark:text-emerald-300" data-testid={`${testId}-outcome`}>Resolved · {action.outcome} by {action.resolved_by}</div>
        ) : (
          <div className="mt-2 flex flex-wrap justify-end gap-1.5">
            {(OUTCOME[action.kind] || OUTCOME.note).map(([o, l]) => (
              <Button key={o} size="sm" variant={o === "reorder" || o === "writeoff" ? "default" : "outline"} className="h-7 text-[11px]"
                      disabled={busy} onClick={() => resolve(o)} data-testid={`${testId}-resolve-${o}`}>
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : l}
              </Button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Ticket-level roll-up: "3 action items open" with the list. */
export function TicketActions({ actions = [], onChanged, testId }) {
  const open = actions.filter((a) => a.status === "open");
  if (!actions.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={testId}>
      <span className={`text-[11px] font-semibold ${open.length ? "text-amber-800 dark:text-amber-300" : "text-muted-foreground"}`} data-testid={`${testId}-count`}>
        {open.length ? `${open.length} action item${open.length === 1 ? "" : "s"} open` : "All action items resolved"}
      </span>
      {actions.map((a) => <ActionPill key={a.id} action={a} onChanged={onChanged} testId={`${testId}-${a.key}`} />)}
    </div>
  );
}
